import type { TFunction } from 'i18next';
import { vendorLabel } from '@/lib/providers/integration-state';

/**
 * Sourcing vendor of a hotel offer/reservation, read from the URN tail the
 * booking composite stamps on everything it returns
 * (`urn:hotel:<id>:vendor:<vendor>`, `urn:offer:<token>:vendor:<vendor>`).
 * The BFF forwards those URNs verbatim, so every hotel list can say WHERE an
 * offer came from (ETG, Sabre, a direct contract, the mock provider) without a
 * seam change — the answer was always in the id.
 */

const VENDOR_TAIL = /:vendor:([a-z0-9-]+)$/i;

/** `urn:hotel:123:vendor:etg` → `etg`; `null` when the URN carries no vendor. */
export function hotelVendor(urn: string | null | undefined): string | null {
  if (!urn) return null;
  const match = VENDOR_TAIL.exec(urn.trim());
  return match ? match[1]!.toLowerCase() : null;
}

/**
 * Vendor stamps that are not an integration of their own. `mock-secondary` is
 * a second stamp on the same mock adapter (booking's scatter-gather dedupe
 * fixture) and `contract` an older spelling of `direct` — the operator is told
 * the provider, not the stamp.
 */
const STAMP_PROVIDER: Readonly<Record<string, string>> = {
  'mock-secondary': 'mock',
  contract: 'direct',
};

/**
 * THE PROVIDER NAME for a hotel URN — the same name the Proveedores screen
 * gives that hotel integration (`vendorLabel`), so the two screens can never
 * disagree about where a room came from. `null` when the URN carries no vendor
 * (older rows): absence of an answer, not a wrong one.
 */
export function hotelProviderName(
  t: TFunction<'network'>,
  urn: string | null | undefined,
): string | null {
  const vendor = hotelVendor(urn);
  if (!vendor) return null;
  return vendorLabel(t, 'hotel', STAMP_PROVIDER[vendor] ?? vendor);
}
